import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Input,
  Button,
  Select,
  DatePicker,
  Form,
  Alert,
  Divider,
  InputNumber,
} from "antd";
import {
  DollarOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { createDividend } from '../services/dividendService';
import { getAllAccounts, getAllStocks } from '../services/stockService';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const DIVIDEND_CALCULATION_FIELDS = ['dividend_amount', 'quantity', 'total_dividend_amount'];

const isPositiveNumber = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0;
const roundTo = (value, decimalPlaces = 2) => Number(value.toFixed(decimalPlaces));

const DividendForm = ({ onSuccess, onCancel, initialData }) => {
  const [form] = Form.useForm();
  const lastEditedDividendFields = useRef([]);
  const [accounts, setAccounts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (initialData) {
      // Convert date string to moment object for DatePicker
      const formData = {
        ...initialData,
        dividend_date: initialData.dividend_date ? moment(initialData.dividend_date) : null,
        total_dividend_amount: isPositiveNumber(Number(initialData.dividend_amount)) && isPositiveNumber(Number(initialData.quantity))
          ? roundTo(Number(initialData.dividend_amount) * Number(initialData.quantity))
          : undefined,
      };
      form.setFieldsValue(formData);
    }
  }, [initialData, form]);

  const loadInitialData = async () => {
    try {
      const [accountsData, stocksData] = await Promise.all([
        getAllAccounts(),
        getAllStocks()
      ]);
      setAccounts(accountsData.results || []);
      setStocks(stocksData.results || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load accounts and stocks data');
    }
  };

  const handleStockSelect = (stockId) => {
    const selectedStock = stocks.find(stock => stock.id === parseInt(stockId));
    if (selectedStock) {
      form.setFieldsValue({
        stock_name: selectedStock.stock_name,
        stock_symbol: selectedStock.stock_symbol
      });
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const submitData = {
        ...values,
        dividend_date: values.dividend_date.format('YYYY-MM-DD'),
      };
      delete submitData.total_dividend_amount;

      await createDividend(submitData);
      setSuccess('Dividend information has been successfully saved!');
      form.resetFields();

      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (error) {
      console.error('Error creating dividend:', error);
      setError(error.response?.data?.error || 'Failed to create dividend record');
    } finally {
      setLoading(false);
    }
  };

  const handleDividendValuesChange = (changedValues, allValues) => {
    const changedDividendField = DIVIDEND_CALCULATION_FIELDS.find(field =>
      Object.prototype.hasOwnProperty.call(changedValues, field)
    );

    if (!changedDividendField) {
      return;
    }

    lastEditedDividendFields.current = [
      ...lastEditedDividendFields.current.filter(field => field !== changedDividendField),
      changedDividendField,
    ].slice(-2);

    if (lastEditedDividendFields.current.length < 2) {
      return;
    }

    const [firstField, secondField] = lastEditedDividendFields.current;
    const firstValue = Number(allValues[firstField]);
    const secondValue = Number(allValues[secondField]);

    if (!isPositiveNumber(firstValue) || !isPositiveNumber(secondValue)) {
      return;
    }

    const targetField = DIVIDEND_CALCULATION_FIELDS.find(field =>
      !lastEditedDividendFields.current.includes(field)
    );

    const dividendAmount = Number(allValues.dividend_amount);
    const quantity = Number(allValues.quantity);
    const totalDividendAmount = Number(allValues.total_dividend_amount);

    if (targetField === 'total_dividend_amount' && isPositiveNumber(dividendAmount) && isPositiveNumber(quantity)) {
      form.setFieldsValue({ total_dividend_amount: roundTo(dividendAmount * quantity) });
      return;
    }

    if (targetField === 'quantity' && isPositiveNumber(totalDividendAmount) && isPositiveNumber(dividendAmount)) {
      form.setFieldsValue({ quantity: Math.round(totalDividendAmount / dividendAmount) });
      return;
    }

    if (targetField === 'dividend_amount' && isPositiveNumber(totalDividendAmount) && isPositiveNumber(quantity)) {
      form.setFieldsValue({ dividend_amount: roundTo(totalDividendAmount / quantity) });
    }
  };

  return (
    <Row gutter={[24, 0]} justify="center">
      <Col xs={24} sm={20} md={16} lg={12} xl={10}>
        <Card bordered={false} className="criclebox">
          {/* Header */}
          <div className="number" style={{ marginBottom: '24px' }}>
            <Row align="middle">
              <Col xs={3}>
                <div className="icon-box" style={{ backgroundColor: "#1890ff" }}>
                  <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                </div>
              </Col>
              <Col xs={21}>
                <span style={{ fontSize: '14px', color: '#666' }}>Portfolio Management</span>
                <Title level={3} style={{ margin: 0 }}>
                  Dividend Entry
                </Title>
                <Text style={{ color: '#666' }}>
                  Enter ETF dividend payment records
                </Text>
              </Col>
            </Row>
          </div>

          <Divider />

          {/* Alerts */}
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}
          
          {success && (
            <Alert
              message="Success"
              description={success}
              type="success"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={handleDividendValuesChange}
            size="large"
          >
            {/* Stock Selection */}
            <Title level={5} style={{ marginBottom: '12px' }}>Stock Information</Title>
            
            <Form.Item label="Select from existing stocks">
              <Select
                placeholder="Select a stock"
                onChange={handleStockSelect}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = typeof option?.label === 'string' ? option.label : '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                options={stocks.map((stock) => ({
                  key: stock.id,
                  value: stock.id,
                  label: `${stock.stock_name} (${stock.stock_symbol})`,
                }))}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="stock_name"
                  label="Stock Name"
                  rules={[{ required: true, message: 'Please enter stock name' }]}
                >
                  <Input placeholder="e.g., KODEX US Dividend Dow Jones Target Covered Call" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="stock_symbol"
                  label="Stock Symbol"
                >
                  <Input placeholder="e.g., 483290" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Account Selection */}
            <Title level={5} style={{ marginBottom: '12px' }}>Account Information</Title>
            
            <Form.Item label="Select from existing accounts">
              <Select
                placeholder="Select an account"
                onChange={(value) => {
                  const selectedAccount = accounts.find(account => account.id === parseInt(value));
                  if (selectedAccount) {
                    const displayName = selectedAccount.account_number 
                      ? `${selectedAccount.account_name} (${selectedAccount.account_number})`
                      : selectedAccount.account_name;
                    form.setFieldsValue({ account_name: displayName });
                  }
                }}
                allowClear
              >
                {accounts.map((account) => (
                  <Option key={account.id} value={account.id}>
                    {account.account_name} {account.account_number && `(${account.account_number})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="account_name"
              label="Account Name"
              rules={[{ required: true, message: 'Please enter account name' }]}
            >
              <Input placeholder="e.g., Account Name (64****26-01)" />
            </Form.Item>

            <Divider />

            {/* Amount and Quantity */}
            <Title level={5} style={{ marginBottom: '12px' }}>Dividend Information</Title>
            
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="dividend_amount"
                  label="Dividend Amount (KRW)"
                  extra="Per-share dividend amount"
                  rules={[
                    { required: true, message: 'Please enter dividend amount' },
                    { type: 'number', min: 0.01, message: 'Please enter a value greater than 0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="₩ 32"
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="quantity"
                  label="Holding Quantity (shares)"
                  rules={[
                    { required: true, message: 'Please enter holding quantity' },
                    { type: 'number', min: 1, message: 'Please enter a value of 1 or greater' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="14"
                    min={1}
                    precision={0}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="total_dividend_amount"
                  label="Total Dividend Amount (KRW)"
                  extra="Auto-calculated from any two values"
                  rules={[
                    { type: 'number', min: 0.01, message: 'Please enter a value greater than 0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="₩ 448"
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="dividend_date"
              label="Dividend Payment Date"
              rules={[{ required: true, message: 'Please select payment date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Select payment date"
                format="YYYY-MM-DD"
              />
            </Form.Item>

            <Divider />

            {/* Buttons */}
            <Form.Item style={{ marginBottom: 0 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SaveOutlined />}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    type="default"
                    onClick={onCancel}
                    icon={<CloseOutlined />}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default DividendForm;
